import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Mail, AlertCircle, Phone, Eye, EyeOff } from 'lucide-react';
import Recaptcha from '../../common/Recaptcha';

interface LoginFormProps {
  onSuccess?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
  const recaptchaRef = useRef<any>(null);
  const { login, loginWithPhone } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!recaptchaToken) {
      setRecaptchaError('Please complete the reCAPTCHA verification');
      return;
    }

    try {
      setError('');
      setRecaptchaError(null);
      setLoading(true);

      if (loginMethod === 'email') {
        await login(identifier, password);
      } else {
        await loginWithPhone(identifier, password);
      }

      onSuccess?.();
    } catch (err: any) {
      if (err.message === 'No user found with this phone number') {
        setError('No account found with this phone number. Please check your phone number or try logging in with email.');
      } else if (err.message === 'User found but no email associated with this phone number') {
        setError('Phone number found but no email associated. Please contact support.');
      } else {
        setError('Failed to log in. Please check your credentials.');
      }
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }

    setLoading(false);
  };

  const handleMethodChange = (method: 'email' | 'phone') => {
    setLoginMethod(method);
    setIdentifier('');
    setError('');
  };

  return (
    <div className="w-full">
      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-1">Sign in to your account</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">Welcome back to CafeLawn</p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-5 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      <div className="mb-5">
        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <button
            type="button"
            onClick={() => handleMethodChange('email')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
              loginMethod === 'email'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-1.5" />
            Email
          </button>
          <button
            type="button"
            onClick={() => handleMethodChange('phone')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
              loginMethod === 'phone'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            <Phone className="w-4 h-4 inline mr-1.5" />
            Phone
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
            {loginMethod === 'email' ? 'Email Address' : 'Phone Number'}
          </label>
          <input
            type={loginMethod === 'email' ? 'email' : 'tel'}
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder={loginMethod === 'email' ? 'Enter your email' : 'Enter your phone number'}
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="w-full px-3 py-2.5 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div>
          {recaptchaError && (
            <p className="text-sm text-red-500 mb-2">{recaptchaError}</p>
          )}
          <Recaptcha onVerify={setRecaptchaToken} onError={setRecaptchaError} theme="light" className="flex justify-center" />
        </div>

        <button
          type="submit"
          disabled={loading || !recaptchaToken}
          className="w-full py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 text-white font-medium rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
        Don&apos;t have an account?{' '}
        <Link to="/signup" className="font-semibold text-purple-600 hover:text-purple-500">
          Sign up
        </Link>
      </p>
    </div>
  );
};

export default LoginForm;
